<script>
export default {
	name: 'ProfileCompletionCard',
	data() {
		return {
			displayedCompletion: 0,
			progressAnimationFrame: null
		}
	},
	props: {
		completion: {
			type: Number,
			default: 0
		}
	},
	computed: {
		normalizedCompletion() {
			const value = Number(this.completion)
			if (!Number.isFinite(value)) return 0
			return Math.min(100, Math.max(0, Math.round(value)))
		},
		progressStyle() {
			return { '--profile-completion': `${this.displayedCompletion * 3.6}deg` }
		},
		animatedCompletion() {
			return Math.round(this.displayedCompletion)
		},
		completionStatus() {
			if (this.animatedCompletion === 100) return 'complete'
			if (this.animatedCompletion >= 50) return 'warning'
			return 'danger'
		},
		completionHintKey() {
			return this.normalizedCompletion === 100
				? 'myArea.headerInfo.completionCompleteHint'
				: 'myArea.headerInfo.completionHint'
		}
	},
	watch: {
		normalizedCompletion(newValue) {
			this.animateCompletion(newValue)
		}
	},
	mounted() {
		this.animateCompletion(this.normalizedCompletion)
	},
	beforeUnmount() {
		if (this.progressAnimationFrame !== null)
			window.cancelAnimationFrame(this.progressAnimationFrame)
	},
	methods: {
		animateCompletion(target) {
			if (this.progressAnimationFrame !== null)
				window.cancelAnimationFrame(this.progressAnimationFrame)

			if (target === this.displayedCompletion) {
				this.progressAnimationFrame = null
				return
			}

			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
				this.displayedCompletion = target
				this.progressAnimationFrame = null
				return
			}

			const start = this.displayedCompletion
			const difference = target - start
			const duration = 700
			let startTime = null

			const updateProgress = (timestamp) => {
				if (startTime === null) startTime = timestamp

				const elapsed = Math.min((timestamp - startTime) / duration, 1)
				const easedProgress = 1 - Math.pow(1 - elapsed, 3)
				this.displayedCompletion = start + (difference * easedProgress)

				if (elapsed < 1) {
					this.progressAnimationFrame = window.requestAnimationFrame(updateProgress)
					return
				}

				this.displayedCompletion = target
				this.progressAnimationFrame = null
			}

			this.progressAnimationFrame = window.requestAnimationFrame(updateProgress)
		}
	}
}
</script>

<template>
	<article class="profile-info-card profile-completion-card" data-qa="profile-completion-card">
		<div
			class="profile-completion-card__progress"
			data-qa="profile-completion-progress"
			:class="`profile-completion-card__progress--${completionStatus}`"
			:style="progressStyle"
			role="progressbar"
			aria-valuemin="0"
			aria-valuemax="100"
			:aria-valuenow="normalizedCompletion"
			:aria-label="$t('myArea.headerInfo.completionAriaLabel')"
		>
			<span data-qa="profile-completion-value-text">{{ animatedCompletion }}%</span>
		</div>
		<div class="profile-completion-card__content">
			<h2 data-qa="profile-completion-title">{{ $t('myArea.headerInfo.completionTitle', { completion: normalizedCompletion }) }}</h2>
			<p>{{ $t(completionHintKey) }}</p>
		</div>
	</article>
</template>

<style lang="sass" scoped>
.profile-completion-card
	display: flex
	align-items: center
	gap: 1rem

	&__progress
		--profile-completion-color: var(--lg-red-active)
		position: relative
		display: grid
		place-items: center
		flex: 0 0 4.5rem
		width: 4.5rem
		height: 4.5rem
		border-radius: 50%
		background: conic-gradient(var(--profile-completion-color) var(--profile-completion), var(--lg-mid-gray-200) 0)

		&--warning
			--profile-completion-color: var(--lg-yellow-review)

		&--complete
			--profile-completion-color: var(--lg-green-validation)

		&::before
			content: ''
			position: absolute
			inset: 0.3125rem
			border-radius: inherit
			background-color: var(--lg-light-gray-100)

		span
			position: relative
			font-family: var(--lgei-headline)
			font-size: 1.25rem
			font-weight: 600
			line-height: 1
			color: var(--profile-completion-color)
			transition: color 0.2s ease-in-out

	&__content
		min-width: 0

		h2
			margin: 0
			font-family: var(--lgei-headline)
			font-size: 1.25rem
			font-weight: 400
			line-height: 1
			color: var(--club--title-default)

		p
			margin: 0.25rem 0 0
			font-family: var(--lgei-text)
			font-size: 0.75rem
			font-weight: 600
			line-height: 1.1
			color: var(--lg-mid-gray-200)
</style>
